import { getUrlParams } from 'src/utils/getUrlParams';
import { specificThemeRender } from 'src/utils/specificThemeRender';
import { getThemeComponent } from 'src/utils/themeImports';
import { BOGO_TYPE, OFFER_PAGE_PARAMS, PRODUCT_PAGINATION_KEY } from './constants';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPromoAssociatedIds,
  getPromoAssociatedProductsByTypeAndTypeIds,
} from 'src/redux/actions';
import { PROMO_APPLICABILITY_TYPES } from 'src/constants/promos';
import { RootState } from 'src/redux/reducers';
import ProductList from './components/ProductList';
import OfferPageHeader from './components/OfferPageHeader';
import VariantModal from 'src/components/VariantModal';
import VariantChooseRepeatModal from 'src/components/VariantChooseRepeatModal';
import VariantSelectedListModal from 'src/components/VariantSelectedListModal';
import QuickViewModal from 'src/components/DesignSystem/QuickViewModal';
import { ProductCustomizationPopUp } from 'src/components/ProductCustomizationPopUp';
import { FullPageLoader } from 'src/components/FullPageLoader/FullPageLoader';

const INITIAL_PAGINATION_DATA = {
  products: [],
  nextPage: 1,
  isNextPage: true,
};

const OfferPage = ({ storeInfo }) => {
  const dispatch = useDispatch();

  const { storeId, personalizedProduct } = useSelector((state: RootState) => ({
    storeId: state.storeReducer.store.store_id,
    personalizedProduct: state.catalogReducer?.personalizedProduct,
  }));

  const [pagination, setPagination] = useState({});
  const [sectionsData, setSectionsData] = useState([]);
  const [allProductIdsData, setAllProductIdsData] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);

  const [isRewardType, setIsRewardType] = useState(false);
  const [promoApplicabilityType, setApplicabilityPromoType] = useState(null);
  const [promoTexts, setPromoTexts] = useState(null);

  useEffect(() => {
    let {
      promo_id,
      is_reward = false,
      type_id,
    } = getUrlParams([
      OFFER_PAGE_PARAMS.PROMO_ID,
      OFFER_PAGE_PARAMS.IS_REWARD,
      OFFER_PAGE_PARAMS.TYPE_ID,
    ]) as any;
    is_reward = !!+is_reward;

    setSelectedSectionId(type_id);
    setIsRewardType(is_reward);
    if (promo_id) {
      getSectionOrAllProductIdsData(+promo_id, is_reward);
    }
  }, []);

  // scroll a section into view by id
  useEffect(() => {
    setTimeout(() => {
      if (selectedSectionId) {
        const element = document.getElementById(selectedSectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, 500);
  }, [selectedSectionId]);

  // function for setting up initial pagination for sections - collections/categories/tags
  function setupInitialPaginationForSections(sectionsDataFromParams = []) {
    const newPagination = {};
    sectionsDataFromParams.forEach((sectionData) => {
      newPagination[sectionData?.id] = INITIAL_PAGINATION_DATA;
    });
    setPagination(newPagination);
  }

  // fetch section's ids or product ids
  function getSectionOrAllProductIdsData(promoId, isReward) {
    const params = {
      promo_id: promoId,
      store_id: storeInfo?.store_id,
      type: isReward ? BOGO_TYPE.Y : BOGO_TYPE.X,
    };
    dispatch(
      getPromoAssociatedIds(params, (data) => {
        setApplicabilityPromoType(data?.applicabilities?.type);
        setPromoTexts({ heading: data?.heading, subHeading: data?.subheading });
        if (data?.applicabilities?.type === PROMO_APPLICABILITY_TYPES.PRODUCT) {
          setAllProductIdsData(data?.applicabilities?.info || []);
          // set pagination for products
          setPagination({ [PRODUCT_PAGINATION_KEY]: INITIAL_PAGINATION_DATA });
        } else {
          setSectionsData(data?.applicabilities?.info || []);
          // set pagination for sections
          setupInitialPaginationForSections(data?.applicabilities?.info || []);
        }
      })
    );
  }

  function updatePaginationData(products = [], isNextPage = false, paginationKey) {
    let curentActiveIdPagination = JSON.parse(JSON.stringify(pagination[paginationKey]));
    curentActiveIdPagination = {
      products: [...(curentActiveIdPagination?.products || []), ...products],
      isNextPage: isNextPage,
      nextPage: curentActiveIdPagination?.nextPage + 1,
    };
    setPagination((oldPagination) => ({
      ...oldPagination,
      [paginationKey]: curentActiveIdPagination,
    }));
  }

  /**
   * @param paginationKey - this is basically the section id (collection, category, tag) or the pagination key for Products only.
   */
  function loadMoreProducts(paginationKey) {
    const isProductsOnlyList = paginationKey === PRODUCT_PAGINATION_KEY;

    const paginationData = pagination[paginationKey];
    if (!paginationData?.isNextPage) {
      return;
    }
    const payload = {
      store_id: storeId,
      page: paginationData?.nextPage,
      type: promoApplicabilityType,
      type_ids: isProductsOnlyList
        ? allProductIdsData?.map((data) => data?.id)
        : [paginationKey],
    };

    dispatch(
      getPromoAssociatedProductsByTypeAndTypeIds(
        payload,
        (products = [], isNextPage = false) =>
          updatePaginationData(products, isNextPage, paginationKey)
      )
    );
  }

  const storeTheme = specificThemeRender(storeInfo);

  const { Header: headerComponents = [], Footer: footerComponents = [] } =
    storeTheme?.components || {};

  return (
    <>
      <div className="tw-flex tw-w-full tw-flex-col">
        {headerComponents.slice(0, 1)?.map((layout) => {
          return getThemeComponent(layout.sub_type, layout.name, layout);
        })}
        <div className="tw-border-b tw-border-solid tw-border-[#DDDDDD] tw-py-[28px] md:tw-py-[42px] ">
          <OfferPageHeader isReward={isRewardType} offerDetails={promoTexts} />
        </div>
        <div className="tw-mx-[16px] tw-max-w-[1000px] tw-py-[24px] md:tw-mx-auto  md:tw-w-full md:tw-py-[42px]">
          {promoApplicabilityType === PROMO_APPLICABILITY_TYPES.PRODUCT ? (
            <ProductList
              id={PRODUCT_PAGINATION_KEY}
              name=""
              loadMoreProducts={loadMoreProducts}
              pagination={pagination}
            />
          ) : (
            <>
              {sectionsData?.map((sectionsData, index) => (
                <ProductList
                  key={index}
                  id={sectionsData.id}
                  name={sectionsData?.name || ''}
                  loadMoreProducts={loadMoreProducts}
                  pagination={pagination}
                />
              ))}
            </>
          )}
        </div>
        {footerComponents.map((layout) => {
          return getThemeComponent(layout.sub_type, layout.name, layout);
        })}
      </div>
      <VariantModal />
      <VariantChooseRepeatModal />
      <VariantSelectedListModal />
      <QuickViewModal />
      <FullPageLoader />
      {personalizedProduct?.personalizationPopUp && <ProductCustomizationPopUp />}
    </>
  );
};

export default OfferPage;
